import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock } from "lucide-react";
import api, { formatARS } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentResult() {
  const [sp] = useSearchParams();
  const orderId = sp.get("order_id");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!orderId) return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        if (!active) return;
        setOrder(data);
        if (data.status === "paid") {
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
      } catch (e) { console.error("PaymentResult poll error:", e); }
      if (active) setTimeout(poll, 2500);
    };
    poll();
    return () => { active = false; };
  }, [orderId]);

  const paid = order?.status === "paid";

  return (
    <div className="px-4 py-20 max-w-2xl mx-auto">
      <div className="museum-frame p-10 text-center">
        {paid ? (
          <>
            <CheckCircle2 className="mx-auto text-[#4C7A51] mb-5" size={40} />
            {order.kind === "event" ? (
              <>
                <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl text-[#171719]">Entrada confirmada</h1>
                <p className="text-[#565A61] mt-3">Tu lugar está reservado. Te esperamos.</p>
                <div className="mt-8 text-left border-t border-[#C4C7CD] pt-6">
                  {order.items?.map((i, idx) => (
                    <div key={idx} className="flex items-center justify-between py-3 border-b border-[#C9CBD1]">
                      <div className="text-[#171719] font-display font-bold uppercase tracking-tight">{i.name}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl text-[#171719]">Pieza en tu archivo</h1>
                <p className="text-[#565A61] mt-3">Tu pago fue confirmado. Estas piezas ahora forman parte de tu archivo personal.</p>
                <div className="mt-8 text-left border-t border-[#C4C7CD] pt-6">
                  <div className="dossier-label text-[#565A61] mb-4">Códigos únicos de tus piezas</div>
                  {order.items?.map((i) => (
                    <div key={i.unit_id} className="flex items-center justify-between py-3 border-b border-[#C9CBD1]">
                      <div>
                        <div className="text-[#171719] font-display font-bold uppercase tracking-tight">{i.name}</div>
                        <div className="dossier-label text-[#565A61] mt-1">{i.unit_code} · talle {i.size} · ej. {i.edition_number}</div>
                      </div>
                      <Link to={`/pieza/${i.unit_code}`} className="dossier-label text-[#565A61] hover:text-[#171719]">ver ficha</Link>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-3 justify-center mt-8">
              <Link to="/mi-archivo" data-testid="goto-archive" className="btn-ink px-7 py-3 dossier-label">Ir a Mi Archivo</Link>
              <Link to={order.kind === "event" ? "/eventos" : "/archivo"} className="btn-outline-ink px-7 py-3 dossier-label">Seguir explorando</Link>
            </div>
          </>
        ) : (
          <>
            <Clock className="mx-auto text-[#565A61] mb-5 animate-pulse" size={40} />
            <h1 className="font-display font-bold uppercase tracking-tight text-2xl text-[#171719]">Verificando el pago…</h1>
            <p className="text-[#565A61] mt-3 text-sm">Estamos confirmando tu pedido desde el servidor. No cierres esta ventana.</p>
          </>
        )}
      </div>
    </div>
  );
}
